import type { Resource } from '__types__';
import { EyeOff, Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { EnterpriseWorkspace } from 'v5/api/enterprise/enterprise.api';
import {
  getContactDetailPath,
  type WorkspaceRef
} from 'v5/core/app-scope/entityWorkspaceRouting';
import { EnterpriseWorkspaceIcon } from 'v5/features/core/enterprise/EnterpriseWorkspaceIcon';
import { Badge } from 'v5/design-system/Badge';

export type ResourceWorkspacesCellProps = {
  organizationId: string;
  resource: Pick<Resource, 'enterprise_workspaces' | 'id'>;
  workspaces: EnterpriseWorkspace[] | WorkspaceRef[] | undefined;
  /** For a host that must keep its page, like a dialog with a draft. */
  openInNewTab?: boolean;
};

function workspaceById(
  workspaces: EnterpriseWorkspace[] | WorkspaceRef[] | undefined,
  id: number
): (EnterpriseWorkspace | WorkspaceRef) | undefined {
  return workspaces?.find(workspace => workspace.id === id);
}

function workspaceLabel(
  workspace: EnterpriseWorkspace | WorkspaceRef | undefined,
  workspaceId: number
): string {
  if (workspace && 'name' in workspace && workspace.name) {
    return workspace.name;
  }
  if (workspace && 'slug' in workspace && workspace.slug) {
    return workspace.slug;
  }
  return `#${workspaceId}`;
}

function workspaceIconUrl(
  workspace: EnterpriseWorkspace | WorkspaceRef | undefined
): string {
  if (workspace && 'iconUrl' in workspace && workspace.iconUrl) {
    return workspace.iconUrl;
  }
  return '';
}

export function ResourceWorkspacesCell({
  organizationId,
  resource,
  workspaces,
  openInNewTab
}: ResourceWorkspacesCellProps) {
  const { t } = useTranslation('resources', {
    keyPrefix: 'ResourceWorkspacesCell'
  });

  const membershipIds = (resource.enterprise_workspaces ?? []).map(
    entry => entry.enterprise_workspace_id
  );

  if (membershipIds.length === 0) {
    return (
      <Badge
        variant="attention"
        tone="subtle"
        size="sm"
        className="font-normal"
      >
        <EyeOff />
        {t('hidden')}
      </Badge>
    );
  }

  if (membershipIds.length === 1 && membershipIds[0] == null) {
    return (
      <Badge variant="info" tone="subtle" size="sm" className="font-normal">
        <Globe />
        {t('global')}
      </Badge>
    );
  }

  const assigned = membershipIds.filter(
    (id): id is number => id != null && Number.isFinite(id)
  );
  if (assigned.length === 0) {
    return <span className="text-sm text-fg-muted">—</span>;
  }

  return (
    <span className="flex min-w-0 flex-wrap items-center gap-1.5 py-1">
      {assigned.map(workspaceId => {
        const workspace = workspaceById(workspaces, workspaceId);
        const label = workspaceLabel(workspace, workspaceId);
        return (
          <Link
            key={workspaceId}
            to={getContactDetailPath(
              organizationId,
              { enterprise_workspace_id: workspaceId },
              workspaces,
              resource.id
            )}
            target={openInNewTab ? '_blank' : undefined}
            rel={openInNewTab ? 'noreferrer' : undefined}
            className="inline-flex max-w-full items-center gap-1 rounded-md bg-bg-subtle px-2 py-0.5 text-xs text-fg hover:bg-bg-muted"
            onClick={e => e.stopPropagation()}
          >
            <EnterpriseWorkspaceIcon
              name={label}
              iconUrl={workspaceIconUrl(workspace)}
              size="sm"
            />
            <span className="truncate">{label}</span>
          </Link>
        );
      })}
    </span>
  );
}
